
import { getAddressLookupData, getSuburb } from '@/api';
import { User } from '@/models/main';
import { JBStopDates, JBAdvancedDeliveryDetailsStop, UIStateJobBooking, ISelectSuburb } from '@/models/jbAdvanced';
import { AddressBookLookupData } from '@/models/addressBook';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import * as routeNames from '@/router/routeNames';

import { clearPhoneNumber } from '@/utils/InputProcessingUtilities';

import Button from '@/components/UI/button/Button.vue';
import DateTimes from '@/components/jobBooking/DateTimes/DateTimes.vue';
import Icon from '@/components/UI/Icon.vue';
import LookupSuburb from '@/components/UI/lookups/LookupSuburb.vue';

@Component({
  components: {
    Button,
    DateTimes,
    Icon,
    LookupSuburb,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class DeliveryDetails extends Vue {
  @Prop(Boolean) private stop3: boolean;
  @Prop(String) private customerCode: string;
  @Prop(Array) private PStops: JBAdvancedDeliveryDetailsStop[];
  @Prop(Array) private dateTimes: JBStopDates[];
  @Prop(Object) private uiState: UIStateJobBooking;

  public routeNames = routeNames;
  private addresses: AddressBookLookupData[] = [];
  private addressesWithoutWarehousesNames: string[] = [];
  private addressesWarehousesNames: string[] = [];
  private addressChosen = {
    0: false,
    1: false,
    2: false,
  };

  private tabItemsCount = 22;
  private tabItemsPrevCount = 7;

  private autocompleteSuburb = getSuburb;

  private defaultStopValue: JBAdvancedDeliveryDetailsStop = {
    id: 0,
    suburb: '',
    suburbId: -1,
    state: '',
    postCode: '',
    notesPhone: '',
    notesName: '',
    notesInstruction: '',
    warehouse: false,
  };
  private stops: JBAdvancedDeliveryDetailsStop[] = [
    { ...this.defaultStopValue, id: 0 },
    { ...this.defaultStopValue, id: 1 },
  ];
  private user: User;

  // Input rules:
  public warehouseContactRules(value, stop, name) {
    if (!this.stops[stop - 1].warehouse) {
      if (value.length === 0) {
        return `${name} is required field`;
      }
    }
    return true;
  }

  private toggleWarehouse(stop): void {
    // Set deafult time for warehouses
    if (this.stops[stop - 1].warehouse) {
      this.dateTimes[stop - 1].timeIsSetted = false;
    }
    // should clear current stop & other stop if it's a warehouse
    if (stop === 1) {
      // clear current
      if (this.addressChosen[0]) {
        this.clear(0);
      }
      // clear 2nd if is a warehouse
      if (this.stops[1].warehouse && this.addressChosen[1]) {
        this.clear(1);
      }
      // switch toggle on 2nd stop
      if (this.stops[1].warehouse) {
        this.stops[1].warehouse = false;
      }
      if (this.stops.length === 3 && !this.stops[0].warehouse) {
        this.$emit('hideStop3');
      }
    }
    if (stop === 2) {
      // clear 1st if is a warehouse
      if (this.stops[0].warehouse && this.addressChosen[0]) {
        this.clear(0);
      }
      // clear current
      if (this.addressChosen[1]) {
        this.clear(1);
      }
      // hide 3rd stop if 2nd is a warehouse
      if (this.stop3) {
        this.$emit('hideStop3');
      }
      // switch toggle on 1st stop
      if (this.stops[0].warehouse) {
        this.stops[0].warehouse = false;
      }
    }
    if (stop === 3) {
      this.clear(2);
      if (this.stops[2].warehouse) {
        this.populateStop3WithWarehouse();
      }
      this.$emit('changeWhCustomerCustomer', !this.stops[2].warehouse);
    }
  }

  /**
   * Switch two stops in the list
   * @param stop - The number of the stop to switch with the next one
   */
  public switchStopClicked(stop: number): void {
    // We can't book Customer -> WH -> WH job, so we are disabling the switch for this case
    if (stop === 2 && this.stops[2]?.warehouse) {
      return;
    }
    const tempStop1 = JSON.stringify(this.stops[stop - 1]);
    const tempStop2 = JSON.stringify(this.stops[stop]);
    this.stops[stop - 1] = JSON.parse(tempStop2);
    this.stops[stop] = JSON.parse(tempStop1);
    this.$emit('changeStops', this.stops);
  }

  private onlyNumberAndSpace($event) {
    const keyCode = $event.keyCode ? $event.keyCode : $event.which;
    if ((keyCode < 48 || keyCode > 57) && keyCode !== 32 && keyCode !== 40 && keyCode !== 41) {
      $event.preventDefault();
    }
  }

  private onlyNumberAndSpaceBlur(stop) {
    this.stops[stop - 1].notesPhone = clearPhoneNumber(this.stops[stop - 1].notesPhone);
  }

  @Watch('PStops', { immediate: true, deep: true })
  private watchToPStops() {
    if (this.PStops.length === 0) {
      this.stops = [
        { ...this.defaultStopValue, id: 0 },
        { ...this.defaultStopValue, id: 1 },
      ];
    } else {
      this.stops = this.PStops;
      this.checkInsterState();
    }
    // check chosen addresses
    this.stops.map((stop, i) => {
      if (stop && stop.name) {
        this.addressChosen[i] = true;
      }
    });
  }
  @Watch('stop3', { immediate: true })
  private toggleStop3value() {
    if (this.stop3 && this.stops.length === 2) {
      this.stops.push({ ...this.stops[0], id: 2 });
    } else if (!this.stop3) {
      this.stops.splice(2, 1);
    }
  }

  private populateStop3WithWarehouse(): void {
    const warehouses = this.addresses.filter(address => address.warehouse);
    const warehouseInChosenState = warehouses.filter(address => address.suburb.state === this.stops[1].state)[0];
    this.stops[2] = {
      id: 2,
      suburb: warehouseInChosenState?.suburb,
      state: warehouseInChosenState?.suburb?.state,
      postCode: warehouseInChosenState?.suburb?.postcode,
      notesPhone: '',
      notesName: '',
      notesInstruction: '',
      name: warehouseInChosenState?.name,
      address_1: warehouseInChosenState?.line1,
      address_2: warehouseInChosenState?.line2,
      warehouse: warehouseInChosenState?.warehouse,
      suburbId: warehouseInChosenState?.suburb?.id,
    };
    if (!this.uiState.stops[2]) {
      this.uiState.stops[2] = {
        id: 2,
        addressBookCheckbox: false,
      };
    }
    const lookup = {
      suburb: warehouseInChosenState?.suburb?.name,
      state: warehouseInChosenState?.suburb?.state,
      postcode: warehouseInChosenState?.suburb?.postcode,
      suburbId: warehouseInChosenState?.suburb?.id,
    };
    this.changeSuburb(2, lookup);
  }

  private addStop(): void {
    if (this.stops[1].state !== '' && !this.stops[1].warehouse) {
      this.$emit('toggleStop3');
      this.populateStop3WithWarehouse();
    }
  }

  private checkInsterState(): void {
    this.stops[0]?.state !== this.stops[1]?.state
      ? this.$emit('changeInterState', true)
      : this.$emit('changeInterState', false);
  }

  private changeSuburb(stop, item) {
    this.stops[stop].suburb = item.suburb || item.name;
    this.stops[stop].suburbId = item.suburbId || item.id;
    this.stops[stop].postCode = item.postcode;
    this.stops[stop].state = item.state;
    this.$emit('changeStops', this.stops);
    // checking for interstate status
    this.checkInsterState();
  }

  private changeDates(dates): void {
    this.$emit('changeDatesRoot', dates);
  }

  private clear(stop: number) {
    if (this.stops[stop]) {
      this.stops[stop].suburb = '';
      this.stops[stop].suburbId = -1;
      this.stops[stop].postCode = '';
      this.stops[stop].state = '';
      this.stops[stop].name = '';
      this.stops[stop].address_1 = '';
      this.stops[stop].address_2 = '';
      this.stops[stop].notesName = '';
      this.stops[stop].notesPhone = '';
      this.stops[stop].notesInstruction = '';
      this.addressChosen[stop] = false;
    }
    if (this.uiState.stops[stop]) {
      this.uiState.stops[stop].addressBookCheckbox = false;
    }
  }
  private clearSuburb(stop: number) {
    if (this.stops[stop]) {
      this.stops[stop].suburb = '';
      this.stops[stop].suburbId = 0;
      this.stops[stop].postCode = '';
      this.stops[stop].state = '';
    }
  }

  private clearAddressBook(stop: number) {
    this.clear(stop);
    if (stop === 0) {
      this.clear(2);
      this.$emit('hideStop3');
    }
    if (stop === 2) {
      this.$emit('hideStop3');
    }
  }

  // getting address book
  // fixme: set prop
  private getAddressBookWrapper(code: string): void {
    getAddressLookupData(code)
      .then((res: AddressBookLookupData[]) => {
        this.addresses = res;
        this.addressesWarehousesNames = [];
        this.addressesWithoutWarehousesNames = [];
        res.forEach((item: AddressBookLookupData) => {
          if (item.warehouse) {
            this.addressesWarehousesNames.push(this.formatAddressName(item.name, item.suburb.name));
          } else {
            this.addressesWithoutWarehousesNames.push(this.formatAddressName(item.name, item.suburb.name));
          }
        });
        this.clearNoneExistentAddresses();
        this.watchToPStops();
      })
      .catch(err => {
        console.log(err);
      });
  }

  // Сonverting an address to `Name, Suburb` format
  private formatAddressName(name: string | undefined, suburb: string | undefined | ISelectSuburb): string {
    if (!name) {
      return '';
    }
    if (!suburb) {
      return name;
    }

    let suburbName = '';
    if (typeof suburb === 'object' && suburb !== null && 'name' in suburb) {
      suburbName = suburb.name;
    } else {
      suburbName = suburb.toString();
    }
    if (name.includes(suburbName)) {
      return name;
    }
    return `${name}, ${suburbName}`;
  }

  private clearNoneExistentAddresses(): void {
    this.stops.map((stop, idx) => {
      const fullName = this.formatAddressName(stop.name, stop.suburb);
      if (
        stop.name &&
        stop.name.length > 0 &&
        this.addressesWarehousesNames.length > 0 &&
        !this.addressesWarehousesNames.includes(fullName) &&
        stop.warehouse
      ) {
        this.clearAddressBook(idx);
        // clear 3rd stop if 2nd was changed
        if (idx === 1) {
          this.clearAddressBook(2);
        }
      }
    });
  }

  @Watch('customerCode', { immediate: true, deep: true })
  private watchToCustomerCode(): void {
    if (this.customerCode) {
      this.getAddressBookWrapper(this.customerCode);
    }
  }

  private keyPressedddressBookName(key, stop) {
    if (key.target.value) {
      this.stops[stop - 1].name = key.target.value.trim();
    }
  }

  private changeAddressBook(address, stop) {
    if (address && stop && this.addresses.length >= 1) {
      const chosenAddress = this.addresses.filter(
        item => `${item.name}, ${item.suburb.name}`.trim() === address.trim(),
      );
      // name, address_1, address_2 – 30 symbols limit
      this.stops[stop - 1].name = address.trim().substring(0, 30);
      if (chosenAddress.length >= 1) {
        this.addressChosen[stop - 1] = true;
        this.stops[stop - 1].name = chosenAddress[0].name.trim().substring(0, 30);
        this.stops[stop - 1].address_1 = chosenAddress[0].line1 ? chosenAddress[0].line1.substring(0, 30) : '';
        this.stops[stop - 1].address_2 = chosenAddress[0].line2 ? chosenAddress[0].line2.substring(0, 30) : '';
        this.stops[stop - 1].suburb = chosenAddress[0].suburb;
        this.stops[stop - 1].suburbId = chosenAddress[0].id;
        this.stops[stop - 1].notesName = chosenAddress[0].note_name ? chosenAddress[0].note_name : '';
        this.stops[stop - 1].notesPhone = chosenAddress[0].note_phone ? chosenAddress[0].note_phone : '';
        this.stops[stop - 1].notesInstruction = chosenAddress[0].note_text ? chosenAddress[0].note_text : '';
        this.stops[stop - 1].warehouse = chosenAddress[0].warehouse;
        const lookup = {
          suburb: chosenAddress[0].suburb.name,
          state: chosenAddress[0].suburb.state,
          postcode: chosenAddress[0].suburb.postcode,
          suburbId: chosenAddress[0].suburb.id,
        };
        this.changeSuburb(stop - 1, lookup);
        if (this.stops[stop - 1].warehouse) {
          this.uiState.stops[stop - 1].addressBookCheckbox = false;
        }
      }
      // clear warehouse stop if changed
      if (chosenAddress.length === 0 && this.stops[stop - 1].warehouse) {
        this.clearNoneExistentAddresses();
        this.stops[stop - 1] = this.defaultStopValue;
      }
      this.$emit('changeStops', this.stops);
    }
  }

  private created(): void {
    this.uiState.stops = this.stops.map((_, idx) => ({
      id: idx + 1,
      addressBookCheckbox: false,
    }));
  }
}
